/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Breadcrumbs,
  Button,
  DataTable,
  InputField,
  SearchWithFilter,
  Select,
  ToggleSwitch,
  type DataTableColumn,
  type FilterSelection,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import {
  getAllProcedures,
  addProcedureSettings,
  updateProcedureSettings,
  deleteProcedureSettings,
  checkProcedureCodeIfExists,
  importProcedureSettings,
  updateProcedureStatus,
} from '@services/api/procedure.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useTheme } from '@context/ThemeContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { ProcedureType } from '@dataTypes/procedure.types'
import { useAppSelector } from '@store/hooks'
import Papa from 'papaparse'
import { formatAmount } from '@utils/numberFormatter.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { logger } from '@utils/safeLogger'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import SettingsDropdownButton from '../../shared/SettingsDropdownButton.component'
import { CATEGORY_BADGE_COLORS, CategoryType } from './ProceduresHelper'
import ImportProcedures from './ImportProcedures.component'
import './ProceduresStyle.css'

type Filters = {
  status: string[]
  categoryType: string[]
}

type ProcedureRow = {
  id: string
  status: boolean
  code: string
  category: string
  fee: string
  feeValue: number
  materials: string
  description: string
}

const EMPTY_FILTERS: Filters = { status: [], categoryType: [] }

const FILTER_LABELS = {
  status: 'Status',
  categoryType: 'Category',
}

const INITIAL_FORM_DATA = {
  procedure_code: '',
  category_id: '',
  clinic_fee: '',
  description: '',
  materials: '',
  treatment_area: '',
}

const readSortValue = (row: ProcedureRow, columnId: string): string => {
  if (columnId === 'status') return row.status ? '0' : '1'
  if (columnId === 'fee') return row.feeValue.toFixed(2).padStart(16, '0')
  return String(row[columnId as keyof ProcedureRow] ?? '').toLowerCase()
}

const Procedures: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [procedures, setProcedures] = useState<ProcedureType[]>([])
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  )
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [searchQuery, setSearchQuery] = useState('')
  const [procedureSettingsToDelete, setProcedureSettingsToDelete] = useState<
    any | null
  >(null)
  const [isAddEditMode, setIsAddEditMode] = useState<boolean>(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] =
    useState<boolean>(false)
  const [formData, setFormData] = useState<any>(INITIAL_FORM_DATA)
  const [validationErrors, setValidationErrors] = useState<any>({})
  const [idToEdit, setIdToEdit] = useState<string | null>(null)
  const [recordToEdit, setRecordToEdit] = useState<any | null>(null)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const [showImportProceduresModal, setShowImportProceduresModal] =
    useState<boolean>(false)
  const [listLoading, setListLoading] = useState(true)
  const [sort, toggleSort] = useTableSort('code')

  useSettingsSubpageLoading(listLoading)

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const proceduresResponse = await getAllProcedures()
      if (
        proceduresResponse.statusCode === 200 &&
        'procedures' in proceduresResponse.data
      ) {
        setProcedures(
          proceduresResponse.data.procedures as unknown as ProcedureType[],
        )
        setCategories(
          Array.from(
            new Map(
              proceduresResponse.data.procedures.map((procedure: any) => [
                procedure.coverage_category.coverage_category_id,
                {
                  id: procedure.coverage_category.coverage_category_id,
                  name: procedure.coverage_category.name,
                },
              ]),
            ).values(),
          ),
        )
      }
    } catch (error) {
      console.error(error)
    } finally {
      setListLoading(false)
    }
  }, [])

  const resetFormData = useCallback((): void => {
    setFormData(INITIAL_FORM_DATA)
    setProcedureSettingsToDelete(null)
    setIdToEdit(null)
    setRecordToEdit(null)
  }, [])

  const gotoListPage = useCallback((): void => {
    resetFormData()
    fetchData()
    setIsAddEditMode(false)
  }, [resetFormData, fetchData])

  useEffect(() => {
    gotoListPage()
  }, [gotoListPage, selectedClinic])

  const cancelAddEdit = useCallback((): void => {
    resetFormData()
    setValidationErrors({})
    setIsAddEditMode(false)
  }, [resetFormData])

  const handleEditClick = (rowData: any): void => {
    setIdToEdit(rowData?.procedure_settings?.procedure_setting_id ?? null)
    setRecordToEdit(rowData)
    setFormData({
      procedure_code: rowData.procedure_code,
      category_id: rowData.coverage_category.coverage_category_id,
      clinic_fee:
        rowData?.procedure_settings?.clinic_fee?.replace('.00', '') ?? 0,
      description: rowData.description,
    })

    setIsAddEditMode(true)
  }

  const handleActiveInactiveProcedure = async (
    rowData: any,
    isActive: boolean,
  ): Promise<void> => {
    showLoading({
      container: 'procedures-settings-page',
    })
    try {
      const response = await updateProcedureStatus(
        rowData.procedure_settings?.procedure_setting_id,
        isActive,
      )
      if (response.statusCode === 200) {
        setTimeout(() => {
          setProcedures((prevProcedures) =>
            prevProcedures.map((procedure: any) =>
              procedure.procedure_settings?.procedure_setting_id ===
              rowData.procedure_settings?.procedure_setting_id
                ? {
                    ...procedure,
                    procedure_settings: {
                      ...procedure.procedure_settings,
                      is_active: isActive,
                    },
                  }
                : procedure,
            ),
          )
          hideLoading('procedures-settings-page')
          showToast(
            createSuccessToast(
              isActive
                ? 'Procedure activated successfully'
                : 'Procedure deactivated successfully',
              isActive
                ? 'Procedure has been activated successfully.'
                : 'Procedure has been deactivated successfully.',
            ),
          )
        }, 1000)
      } else {
        setTimeout(() => {
          hideLoading('procedures-settings-page')
          showToast(
            createFailedToast(
              isActive
                ? 'Procedure not activated'
                : 'Procedure not deactivated',
              isActive
                ? 'Procedure not activated. Please try again.'
                : 'Procedure not deactivated. Please try again.',
            ),
          )
        }, 1000)
      }
    } catch (e) {
      logger.error('Error deleting procedure settings:', e)
      setTimeout(() => {
        hideLoading('procedures-settings-page')
        showToast(
          createFailedToast(
            isActive ? 'Procedure not activated' : 'Procedure not deactivated',
            isActive
              ? 'Procedure not activated. Please try again.'
              : 'Procedure not deactivated. Please try again.',
          ),
        )
      }, 1000)
    }
  }

  const handleStatusChange = (rowData: any, isActive: boolean): void => {
    if (!rowData.procedure_settings) {
      showToast(
        createFailedToast(
          'Procedure not activated',
          'Procedure not activated. Please edit the procedure and complete the clinic fee setup to enable it.',
        ),
      )
      return
    }
    void handleActiveInactiveProcedure(rowData, isActive)
  }

  const onDeleteProcedure = (procedureSettings: any): void => {
    setProcedureSettingsToDelete(procedureSettings)
    setShowConfirmDeleteModal(true)
  }

  const filteredProcedures = procedures
    .filter((procedure: any) => {
      const matchesSearchQuery = [
        procedure.procedure_code,
        procedure.coverage_category.name,
        procedure?.procedure_settings?.clinic_fee ?? 0,
        procedure.materials,
        procedure.description,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      )

      const matchesStatus =
        filters.status.length === 0 ||
        filters.status.includes(
          procedure?.procedure_settings?.is_active ? 'Active' : 'Inactive',
        )

      const matchesCategoryType =
        filters.categoryType.length === 0 ||
        filters.categoryType.includes(procedure.coverage_category.name)

      return matchesSearchQuery && matchesStatus && matchesCategoryType
    })
    .sort((a, b) => a.procedure_code.localeCompare(b.procedure_code))

  const procedureById = useMemo(
    () => new Map(procedures.map((p: any) => [p.procedure_id, p])),
    [procedures],
  )

  const rows: ProcedureRow[] = filteredProcedures.map((p: any) => ({
    id: p.procedure_id,
    status: p?.procedure_settings?.is_active === true,
    code: p.procedure_code,
    category: p.coverage_category?.name ?? '',
    fee: formatAmount(p?.procedure_settings?.clinic_fee, '-'),
    feeValue: Number(p?.procedure_settings?.clinic_fee ?? 0),
    materials: 'N/A',
    description: p.description ?? '',
  }))

  const sortedRows = sortRows(rows, sort, readSortValue)
  const { pageRows, pagination } = usePagedRows(sortedRows, {
    alwaysShow: true,
  })

  const handleChange = (name: string, value: string | number): void => {
    setFormData({
      ...formData,
      [name]: value,
    })
    if (validationErrors[name]) {
      delete validationErrors[name]
    }
  }

  const validateForm = useCallback((): boolean => {
    const errors: any = {}
    if (!formData.procedure_code) {
      errors.procedure_code = 'Procedure code is required'
    }
    if (!formData.category_id) {
      errors.category_id = 'Category is required'
    }
    if (!formData.clinic_fee) {
      errors.clinic_fee = 'Clinic fee is required'
    } else {
      if (formData.clinic_fee <= 0) {
        errors.clinic_fee = 'Clinic fee must be greater than 0'
      }
    }
    if (!formData.description || formData.description.trim() === '') {
      errors.description = 'Description is required'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [
    formData.procedure_code,
    formData.category_id,
    formData.clinic_fee,
    formData.description,
  ])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validateForm()) {
      return
    }
    showLoading({
      container: 'procedures-settings-page',
    })

    try {
      if (!(recordToEdit?.is_system ? true : false)) {
        const procedureCodeExists = await checkProcedureCodeIfExists(
          formData.procedure_code,
          idToEdit ?? undefined,
        )

        if (procedureCodeExists?.data?.result?.exists) {
          setTimeout(() => {
            hideLoading('procedures-settings-page')
            showToast(
              createFailedToast(
                'Procedure code already exists',
                'Procedure code already exists. Please try again with a different procedure code.',
              ),
            )
          }, 2000)
          return
        }
      }

      const response = idToEdit
        ? await updateProcedureSettings(idToEdit, formData)
        : await addProcedureSettings({
            ...formData,
            ...(recordToEdit
              ? { procedure_id: recordToEdit.procedure_id }
              : {}),
          })

      if (response.statusCode === 200) {
        setTimeout(() => {
          hideLoading('procedures-settings-page')
          showToast(
            createSuccessToast(
              idToEdit
                ? 'Procedure updated successfully'
                : 'Procedure added successfully',
              idToEdit
                ? 'Procedure has been updated successfully.'
                : 'Procedure has been added successfully.',
            ),
          )
          gotoListPage()
        }, 2000)
      } else {
        setTimeout(() => {
          hideLoading('procedures-settings-page')
          showToast(
            createFailedToast(
              idToEdit
                ? 'Procedure updating failed'
                : 'Procedure adding failed',
              idToEdit
                ? 'Procedure updating failed. Please try again later.'
                : 'Procedure adding failed. Please try again later.',
            ),
          )
        }, 2000)
      }
    } catch (error) {
      console.error(error)
      hideLoading('procedures-settings-page')
      showToast(
        createFailedToast('Procedure save failed', 'Please try again later.'),
      )
    }
  }, [
    validateForm,
    showLoading,
    recordToEdit,
    idToEdit,
    formData,
    hideLoading,
    showToast,
    gotoListPage,
  ])

  const handleDeleteProcedureSettings = useCallback(async (): Promise<void> => {
    if (procedureSettingsToDelete) {
      showLoading({
        container: 'procedures-settings-page',
      })
      try {
        const response = await deleteProcedureSettings(
          procedureSettingsToDelete.procedure_setting_id,
        )
        if (response.statusCode === 200) {
          setTimeout(() => {
            hideLoading('procedures-settings-page')
            showToast(
              createSuccessToast(
                'Procedure settings deleted successfully',
                'Procedure settings have been deleted successfully.',
              ),
            )
            gotoListPage()
          }, 1000)
        } else {
          setTimeout(() => {
            hideLoading('procedures-settings-page')
            showToast(
              createFailedToast(
                'Procedure settings not deleted',
                'Procedure settings not deleted. Please try again.',
              ),
            )
          }, 1000)
        }
      } catch (e) {
        logger.error('Error deleting procedure settings:', e)
        setTimeout(() => {
          hideLoading('procedures-settings-page')
          showToast(
            createFailedToast(
              'Procedure settings not deleted',
              'Procedure settings not deleted. Please try again.',
            ),
          )
        }, 1000)
      } finally {
        setShowConfirmDeleteModal(false)
      }
    }
  }, [
    procedureSettingsToDelete,
    showLoading,
    hideLoading,
    showToast,
    gotoListPage,
  ])

  const handleImportData = (file: File): void => {
    showLoading({
      container: 'procedures-settings-page',
    })
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        const importData = results.data.map((x: any) => ({
          code: x.code,
          description: x.description,
          clinic_fee: Number(x.clinic_fee),
        }))

        const response = await importProcedureSettings(importData)
        if (
          response.statusCode === 200 &&
          'procedure_settings' in response.data
        ) {
          setTimeout(() => {
            hideLoading('procedures-settings-page')
            showToast(
              createSuccessToast('Import successful', 'Import successful.'),
            )
            setShowImportProceduresModal(false)
            gotoListPage()
          }, 1000)
        } else {
          setTimeout(() => {
            showToast(
              createFailedToast(
                'Import failed',
                'Import failed. Please try again.',
              ),
            )
          }, 1000)
        }
      },
      error: function (err) {
        logger.error('Error parsing CSV:', err)
        showToast(
          createFailedToast('Error parsing CSV', 'Please try again later.'),
        )
      },
    })
  }

  const handleExportData = useCallback((): void => {
    const data = procedures.map((x: any) => ({
      code: x.procedure_code,
      clinic_fee: x.procedure_settings?.clinic_fee ?? 0,
      description: x.description,
    }))
    if (!data || data.length === 0) {
      showToast(
        createFailedToast(
          'No data to export',
          'No data to export. Please try again.',
        ),
      )
      return
    }

    try {
      const csv = Papa.unparse(data)
      const BOM = '\uFEFF'
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, '-')
      link.setAttribute('href', url)
      link.setAttribute('download', `data-export-${timestamp}.csv`)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      showToast(createSuccessToast('Export successful', 'Export successful.'))
    } catch (error) {
      console.error(error)
      showToast(createFailedToast('Export failed', 'Please try again later.'))
    }
  }, [procedures, showToast])

  useEffect(() => {
    if (!isAddEditMode) return
    setHeaderTitle(
      <Breadcrumbs
        className="pc-breadcrumbs"
        showBackIcon
        onBackClick={cancelAddEdit}
        items={[
          { id: 'list', label: 'Procedure codes', onClick: cancelAddEdit },
          {
            id: 'current',
            label: recordToEdit ? 'Edit procedure' : 'Add procedure',
          },
        ]}
      />,
    )
    return () => setHeaderTitle(null)
  }, [isAddEditMode, recordToEdit, cancelAddEdit, setHeaderTitle])

  useEffect(() => {
    setHeaderButtons(
      isAddEditMode ? (
        <>
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={cancelAddEdit}
          >
            Cancel
          </Button>
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              void handleSave()
            }}
          >
            Submit code
          </Button>
        </>
      ) : (
        <>
          <SearchWithFilter
            className="pc-search"
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={{
              status: ['Active', 'Inactive'],
              categoryType: categories.map((category) => category.name),
            }}
            filterLabels={FILTER_LABELS}
            selectedFilters={filters}
            onFilterChange={(selection: FilterSelection) =>
              setFilters({
                status: selection.status ?? [],
                categoryType: selection.categoryType ?? [],
              })
            }
            showResetButton
            onReset={() => {
              setSearchQuery('')
              setFilters(EMPTY_FILTERS)
            }}
          />
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={handleExportData}
          >
            Export code
          </Button>
          {procedures.length > 0 ? (
            <SettingsDropdownButton
              label="Add code"
              items={[
                {
                  id: 'add',
                  label: 'Add code',
                  onSelect: () => setIsAddEditMode(true),
                },
                {
                  id: 'import',
                  label: 'Import code',
                  onSelect: () => setShowImportProceduresModal(true),
                },
              ]}
            />
          ) : (
            <Button
              className="settings-ds-button"
              variant="secondary"
              size="md"
              type="button"
              onClick={() => setShowImportProceduresModal(true)}
            >
              Import code
            </Button>
          )}
        </>
      ),
    )

    return () => setHeaderButtons(null)
  }, [
    categories,
    cancelAddEdit,
    filters,
    handleExportData,
    handleSave,
    isAddEditMode,
    procedures.length,
    searchQuery,
    setHeaderButtons,
  ])

  const columns: DataTableColumn<ProcedureRow>[] = [
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      width: 96,
      renderCell: (row) => (
        <ToggleSwitch
          aria-label={`${row.code} status`}
          checked={row.status}
          onChange={(checked) =>
            handleStatusChange(procedureById.get(row.id), checked)
          }
        />
      ),
    },
    { id: 'code', label: 'Procedure code', sortable: true, width: 156 },
    {
      id: 'category',
      label: 'Category',
      sortable: true,
      width: 200,
      renderCell: (row) =>
        row.category ? (
          <Badge
            mode={badgeMode}
            color={
              CATEGORY_BADGE_COLORS[row.category as CategoryType] ?? 'gray'
            }
            label={row.category}
          />
        ) : (
          '-'
        ),
    },
    { id: 'fee', label: 'Clinic fee', sortable: true, width: 120 },
    {
      id: 'materials',
      label: 'Materials',
      sortable: true,
      width: 110,
      renderCell: (row) => <span className="pc-muted">{row.materials}</span>,
    },
    { id: 'description', label: 'Description', sortable: true },
  ]

  const isSystemRecord = recordToEdit?.is_system ? true : false
  const hasNoProcedures = procedures.length === 0
  const tableState = listLoading
    ? 'loading'
    : filteredProcedures.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="procedures-settings">
      {isAddEditMode ? (
        <section className="pc-form">
          <InputField
            variant="text"
            label="Procedure code"
            placeholder="Enter code"
            showTooltip
            tooltipLabel="CDT code for this procedure, e.g. D0120"
            value={formData.procedure_code}
            onChange={(e) => handleChange('procedure_code', e.target.value)}
            error={!!validationErrors.procedure_code}
            helperText={validationErrors.procedure_code}
            disabled={isSystemRecord}
            fullWidth
          />
          <Select
            label="Category"
            placeholder="Select"
            showTooltip
            options={categories.map((category) => ({
              value: category.id,
              label: category.name,
            }))}
            value={formData.category_id}
            onChange={(value) => handleChange('category_id', String(value))}
            error={!!validationErrors.category_id}
            helperText={validationErrors.category_id}
            disabled={isSystemRecord}
          />
          <InputField
            variant="amount"
            label="Clinic fee"
            placeholder="Enter amount"
            showTooltip
            tooltipLabel="Fee your clinic charges for this procedure"
            value={formData.clinic_fee}
            onChange={(e) =>
              handleChange('clinic_fee', e.target.value.replace(/,/g, ''))
            }
            error={!!validationErrors.clinic_fee}
            helperText={validationErrors.clinic_fee}
            fullWidth
          />
          <InputField
            variant="text"
            label="Description"
            placeholder="Enter description"
            showTooltip
            tooltipLabel="Shown on charts, treatment plans and claims"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!validationErrors.description}
            helperText={validationErrors.description}
            disabled={isSystemRecord}
            fullWidth
          />
          <Select
            label="Materials"
            placeholder="Select"
            optional
            options={[]}
            value={formData.materials}
            onChange={(value) => handleChange('materials', String(value))}
          />
          <Select
            label="Treatment area"
            placeholder="Select"
            optional
            showTooltip
            options={[]}
            value={formData.treatment_area}
            onChange={(value) => handleChange('treatment_area', String(value))}
          />
        </section>
      ) : (
        <DataTable<ProcedureRow>
          className="pc-table"
          columns={columns}
          rows={pageRows}
          state={tableState}
          emptyState={{
            title: 'No procedure codes found',
            description: hasNoProcedures
              ? "You don't have any procedure codes on record yet."
              : 'No procedure codes match your search.',
            actionLabel: hasNoProcedures ? 'Add code' : undefined,
            onAction: hasNoProcedures
              ? () => setIsAddEditMode(true)
              : undefined,
          }}
          sortColumnId={sort.columnId}
          sortDirection={sort.columnId ? sort.direction : undefined}
          onSort={toggleSort}
          actionsLabel="Action"
          renderActions={(row) => {
            const record: any = procedureById.get(row.id)
            return (
              <SettingsRowActions
                disabled={!record}
                deleteDisabled={!!record?.is_system}
                onEdit={() => record && handleEditClick(record)}
                onDelete={() =>
                  record &&
                  onDeleteProcedure({
                    procedure_setting_id:
                      record?.procedure_settings?.procedure_setting_id ?? null,
                    procedure_code: record.procedure_code,
                  })
                }
              />
            )
          }}
          pagination={pagination}
        />
      )}

      {showConfirmDeleteModal && (
        <DeleteConfirmationModal
          show={showConfirmDeleteModal}
          onHide={() => setShowConfirmDeleteModal(false)}
          onConfirm={handleDeleteProcedureSettings}
          title="Delete procedure?"
          bodyText={`Are you sure you want to delete procedure "${procedureSettingsToDelete?.procedure_code ?? ''}"? This action can't be undone.`}
        />
      )}
      {showImportProceduresModal && (
        <ImportProcedures
          show={showImportProceduresModal}
          onHide={() => setShowImportProceduresModal(false)}
          onUpload={handleImportData}
        />
      )}
    </div>
  )
}

export default Procedures
